import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  fetchUserNetworkLists,
  type FetchedNetworkLists,
} from '@/lib/networkListsFetch';
import type { RelayEntry } from '@/lib/appRelays';

/** A single NIP-65 relay entry with optional read/write markers. */
export type UserRelayEntry = RelayEntry;

export type UserNetworkLists = FetchedNetworkLists;

/**
 * Fetch the logged-in user's NIP-65 relay list (kind 10002) and Blossom
 * server list (kind 10063) from the current connection pool (+ user relays).
 */
export function useUserNetworkLists(options?: { enabled?: boolean }) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const pubkey = user?.pubkey;
  const enabled = (options?.enabled ?? true) && !!pubkey;

  return useQuery<UserNetworkLists>({
    queryKey: ['user-network-lists', pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!pubkey) {
        return { relays: [], blossomServers: [] };
      }

      return fetchUserNetworkLists({
        pubkey,
        query: (filters, opts) => nostr.query(filters, opts),
        group: (urls) => {
          try {
            return nostr.group(urls) as { query: typeof nostr.query };
          } catch {
            return undefined;
          }
        },
        signal: AbortSignal.any([signal, AbortSignal.timeout(28_000)]),
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 1,
  });
}

// Re-export for callers that typed against the event shape
export type { NostrEvent };
